import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeCtx {
  mode: ThemeMode;
  resolved: 'light' | 'dark';
  setMode: (m: ThemeMode) => void;
  cycle: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

const DEFAULT_STORAGE_KEY = 'edupra-theme';

function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
}

function applyDocClass(resolved: 'light' | 'dark') {
  const html = document.documentElement;
  if (resolved === 'dark') html.classList.add('dark');
  else html.classList.remove('dark');
  html.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#08080B' : '#FAFAF9');
}

export interface ThemeProviderProps {
  children: React.ReactNode;
  /** localStorage key for persisting the user's mode choice. */
  storageKey?: string;
}

export function ThemeProvider({ children, storageKey = DEFAULT_STORAGE_KEY }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem(storageKey) as ThemeMode | null) ?? 'system';
  });
  const [resolved, setResolved] = useState<'light' | 'dark'>(() =>
    typeof window === 'undefined'
      ? 'light'
      : resolveMode((localStorage.getItem(storageKey) as ThemeMode | null) ?? 'system'),
  );

  useEffect(() => {
    const next = resolveMode(mode);
    setResolved(next);
    applyDocClass(next);
    localStorage.setItem(storageKey, mode);
  }, [mode, storageKey]);

  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const next = mq.matches ? 'dark' : 'light';
      setResolved(next);
      applyDocClass(next);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  const value = useMemo<ThemeCtx>(
    () => ({
      mode,
      resolved,
      setMode: setModeState,
      cycle: () =>
        setModeState((m) => (m === 'system' ? 'light' : m === 'light' ? 'dark' : 'system')),
    }),
    [mode, resolved],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
